<script setup lang="ts">
/**
 * A grid of square `NxMediaCell`s to pick from — stickers, GIFs, picture
 * thumbnails. As many columns as fit at the cell `size`, or exactly
 * `columns`. The grid is one tab stop: the arrow keys walk the cells (Home
 * and End jump to the first and the last), Tab leaves it. Disabled cells are
 * skipped. The cells are buttons, so the grid is a labelled `group`, like the
 * emoji grid of `NxEmojiPicker`.
 *
 * Where the grid scrolls is the caller's: put it into a box with a
 * `max-height` and `overflow: auto`.
 */
import { computed, provide, shallowRef, useTemplateRef } from 'vue';
import { mediaGridKey, type NxMediaCellSize } from './context';

const props = withDefaults(
  defineProps<{
    /** Accessible name of the grid („Sticker“, „GIFs“). */
    label?: string;
    /** Size of the cells; also the minimum column width. Defaults to `s`. */
    size?: NxMediaCellSize;
    /** Fixed column count; the cells then share the width. Unset: as many as fit. */
    columns?: number;
  }>(),
  { label: undefined, size: 's', columns: undefined }
);

interface Entry {
  button: HTMLElement;
  disabled: () => boolean;
}

const root = useTemplateRef<HTMLElement>('root');
const entries = shallowRef<Entry[]>([]);
const active = shallowRef<HTMLElement | null>(null);

function domOrder(a: Entry, b: Entry): number {
  const pos = a.button.compareDocumentPosition(b.button);
  if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
  if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
  return 0;
}

/** Cells in reading order. */
function ordered(): Entry[] {
  return [...entries.value].sort(domOrder);
}

const tabStop = computed<HTMLElement | null>(() => {
  const list = entries.value;
  const current = list.find((e) => e.button === active.value);
  if (current && !current.disabled()) return current.button;
  return [...list].sort(domOrder).find((e) => !e.disabled())?.button ?? null;
});

provide(mediaGridKey, {
  size: computed(() => props.size),
  tabStop,
  register(button, disabled) {
    entries.value = [...entries.value.filter((e) => e.button !== button), { button, disabled }];
  },
  unregister(button) {
    entries.value = entries.value.filter((e) => e.button !== button);
    if (active.value === button) active.value = null;
  },
  activate(button) {
    active.value = button;
  },
});

/** Columns of the laid-out grid: the `columns` prop, else the resolved tracks. */
function columnCount(): number {
  if (props.columns && props.columns > 0) return Math.floor(props.columns);
  const el = root.value;
  if (!el) return 1;
  const tracks = getComputedStyle(el)
    .gridTemplateColumns.split(/\s+/)
    .filter((track) => /^[\d.]+px$/.test(track)).length;
  return Math.max(1, tracks);
}

function focusFrom(list: Entry[], start: number, direction: number) {
  for (let i = start; i >= 0 && i < list.length; i += direction) {
    const entry = list[i]!;
    if (entry.disabled()) continue;
    active.value = entry.button;
    entry.button.focus();
    return;
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  const list = ordered();
  const index = list.findIndex((e) => e.button === event.target);
  // Keys on a cell's corner action (or anything else inside) stay theirs.
  if (index < 0) return;
  const rtl = root.value ? getComputedStyle(root.value).direction === 'rtl' : false;
  const columns = columnCount();
  const steps: Record<string, number> = {
    ArrowRight: rtl ? -1 : 1,
    ArrowLeft: rtl ? 1 : -1,
    ArrowDown: columns,
    ArrowUp: -columns,
  };
  if (event.key in steps) {
    event.preventDefault();
    const step = steps[event.key]!;
    const target = index + step;
    if (target < 0 || target >= list.length) return;
    focusFrom(list, target, Math.sign(step));
  } else if (event.key === 'Home') {
    event.preventDefault();
    focusFrom(list, 0, 1);
  } else if (event.key === 'End') {
    event.preventDefault();
    focusFrom(list, list.length - 1, -1);
  }
}
</script>

<template>
  <div
    ref="root"
    class="nx-media-grid"
    :class="[`size-${props.size}`, { 'has-columns': !!props.columns }]"
    :style="props.columns ? { '--media-grid-columns': String(props.columns) } : undefined"
    role="group"
    :aria-label="props.label"
    data-testid="nx-media-grid"
    @keydown="onKeydown"
  >
    <slot />
  </div>
</template>

<style scoped>
.nx-media-grid {
  --media-grid-min: var(--nxt-area-size-xs);

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--media-grid-min)), 1fr));
  gap: var(--nxt-space-2xs);
}
.nx-media-grid.size-m {
  --media-grid-min: var(--nxt-area-size-sm);
}
.nx-media-grid.has-columns {
  grid-template-columns: repeat(var(--media-grid-columns), minmax(0, 1fr));
}
</style>
